import { useAppDispatch } from "app.hook";
import { FormikErrors, FormikProps, useFormik } from "formik";
import React, { FC, useEffect, useState } from "react";
import { useParams } from "react-router";
import CustomPhoneInput from "shared/components/custom-input/custom-phone-input";
import { MainContainer } from "shared/components/layout/style/layout-internal-style";
import useRefresh from "shared/hooks/use-refresh";
import { getCountriesAPI } from "states/address/address.api";
import { ICountryModel } from "states/address/address.models";
import { createdCustomerAddressesAPI, getCustomerAddressAPI, updatedCustomerAddressesAPI } from "states/customer-address/customer-address.api";
import { AddressTypeEnum, IAddressModel, ICustomerAddressModel } from "states/customer-address/customer-address.models";
import { ConfirmModel } from "states/global-value/global-value.models";
import { closeConfirmModal, setLoading, showConfirmModal, showError, showSuccess } from "states/global-value/global-value.slice";
import { DefaultButton, PrimaryButton } from "themes/style-components/button";
import { FlexAuto, FlexContainer, FlexLeftContainer } from "themes/style-components/flex";
import { FormControl, FormError, FormGroup, FormLabel } from "themes/style-components/form-group";
import { LinkStyle } from "themes/style-components/link";

interface IAddressParams {
  id: string;
}
const AddUpdateAddressPage: FC = (props) => {
  const params = useParams<IAddressParams>();
  const dispatch = useAppDispatch();
  const [countries, setCountries] = useState<ICountryModel[]>([]);
  const [isUpdateMode, setUpdateMode] = useState<boolean>(false);
  const refresh = useRefresh();
  const formik: FormikProps<ICustomerAddressModel> = useFormik<ICustomerAddressModel>({
    initialValues: {
      addressInfo:{
        firstName: "",
        lastName: "",
        phoneNumber: "",
        street: "",
        wardCode: "",
        wardName: "",
        districtCode: "",
        districtName: "",
        provinceCode: "",
        provinceName: "",
        countryCode:"",
        countryName:"",
        stateName:"",
        stateCode:"",
        postalCode: "",
      },
      addressType: document.location.href.indexOf("/billing") > -1 ? AddressTypeEnum.Billing : AddressTypeEnum.Shipping,
    },

    validate: (values) => {
      let errors: FormikErrors<ICustomerAddressModel> = {
       
      };
      if (values.addressInfo.firstName === "") {
        errors.addressInfo = {
          firstName:"Please enter first name"
        };
      }
      if (values.addressInfo.lastName === "") {
        errors.addressInfo = {
          lastName : "Please enter last name"
        };
      }
      if (values.addressInfo.phoneNumber === "") {
        errors.addressInfo ={
          phoneNumber : "Please enter phone number"
        };
      }
      if (values.addressInfo.postalCode === "") {
        errors.addressInfo = {
          postalCode : "Please enter postal code"
        };
      }
      if (values.addressInfo.countryCode === "") {
        errors.addressInfo = {
          countryCode : "Please select country"
        };
      }
      if (values.addressInfo.stateCode === "") {
        errors.addressInfo = {
          stateCode : "Please select state"
        };
      }
      if (values.addressInfo.provinceName === "") {
        errors.addressInfo = {
          provinceName: "Please enter city"
        };
      }
      if (values.addressInfo.street === "") {
        errors.addressInfo = {
          street : "Please enter address"
        };
      }
      return errors;
    },
    onSubmit: async (values) => {
      dispatch(setLoading(true));
      try {
        values.addressInfo.phoneNumber = values.addressInfo?.phoneNumber.replace(/\)/g, '')
          .replace(/\(/g, '')
          .replace(/-/g, '')
          .replace(/ /g, '');
        if (isUpdateMode) {
          await updatedCustomerAddressesAPI(values);
        } else {
          await createdCustomerAddressesAPI(values);
        }
        dispatch(showConfirmModal({
          title: "Notification",
          message: "Updated successfully",
          okText: "Yes",
          onYes: () => {
            refresh();
            dispatch(closeConfirmModal())
          }
        } as ConfirmModel))
        dispatch(setLoading(false));
        dispatch(showSuccess("Updated Data Successfully"));
      } catch (error: any) {
        dispatch(setLoading(false));
        dispatch(showError(error?.response.data.error.message));
      }
    },
  });

  const getCountries = async () => {
    let countries = (await getCountriesAPI()).data;
    setCountries(countries);
  }
  const getAddress = async () => {
    let result = (await getCustomerAddressAPI(Number(params.id))).data;
    formik.setValues(result);
  }
  useEffect(() => {
    document.title = "User Info";
    if (document.location.href.indexOf("/add") > -1) {
      setUpdateMode(false);
    } else {
      setUpdateMode(true);
      getAddress();
    }
    getCountries();
  }, [])
  return (
    <MainContainer>
      <form onSubmit={formik.handleSubmit} >
        <FormGroup className="mb-4">
          <FormLabel htmlFor="firstName" >First Name</FormLabel>
          <FlexAuto>
            <FormControl>
              <input
                id="firstName"
                name="addressInfo.firstName"
                type="text"
                placeholder="First Name"
                onChange={formik.handleChange}
                value={formik.values.addressInfo?.firstName}
              />
            </FormControl>
            {formik.errors.addressInfo?.firstName && (
              <FormError>
                {formik.errors.addressInfo?.firstName}
              </FormError>
            )}
          </FlexAuto>
        </FormGroup>
        <FormGroup className="mb-4">
          <FormLabel htmlFor="lastName" >Last Name</FormLabel>
          <FlexAuto>
            <FormControl>
              <input id="lastName"
                name="addressInfo.lastName"
                type="text"
                placeholder="Last Name"
                onChange={formik.handleChange}
                value={formik.values.addressInfo?.lastName} />
            </FormControl>
            {formik.errors.addressInfo?.lastName && (
              <FormError>
                {formik.errors.addressInfo?.lastName}
              </FormError>
            )}
          </FlexAuto>
        </FormGroup>
        <FormGroup className="mb-4">
          <FormLabel htmlFor="phoneNumber" >Phone Number</FormLabel>
          <FlexAuto>
            <FormControl>
              <CustomPhoneInput
                id="phoneNumber"
                name="addressInfo.phoneNumber"
                onChange={formik.handleChange}
                value={formik.values.addressInfo?.phoneNumber}
              />
            </FormControl>
            {formik.errors.addressInfo?.phoneNumber && (
              <FormError>
                {formik.errors.addressInfo?.phoneNumber}
              </FormError>
            )}
          </FlexAuto>
        </FormGroup>
        <FormGroup className="mb-4">
          <FormLabel htmlFor="country">Country</FormLabel>
          <FlexAuto>
            <FormControl>
              <select id="country"
                name="addressInfo.countryCode"
                onChange={(ev) => {
                  let country = [...countries].find(m => m.countryCode === ev.target.value);
                  formik.setValues({
                    ...formik.values,
                    addressInfo:{
                      ...formik.values.addressInfo,
                      countryCode: ev.target.value,
                      countryName: country?.name ?? "",
                    }
                  })
                }}
                value={formik.values.addressInfo?.countryCode}>
                <option>Country</option>
                {countries?.map((m) => (
                  <option value={m.countryCode} >{m.name}</option>
                ))}
              </select>
            </FormControl>
            {formik.errors.addressInfo?.provinceCode && (
              <FormError>
                {formik.errors.addressInfo?.provinceCode}
              </FormError>
            )}
          </FlexAuto>
        </FormGroup>
        <FormGroup className="mb-4">
          <FormLabel htmlFor="state">State</FormLabel>
          <FlexAuto>
            <FormControl>
              <select id="state"
                name="addressInfo.stateCode"
                onChange={(ev) => {
                  let state = [...countries].find(m => m.countryCode === ev.target.value)?.states?.find(m => m.stateCode === ev.target.value);
                  formik.setValues({
                    ...formik.values,
                    addressInfo:{
                      ...formik.values.addressInfo,
                      stateCode: ev.target.value,
                      stateName: state?.name ?? "",
                    }
                  })
                }}
                value={formik.values.addressInfo?.stateCode}>
                <option>State</option>
                {[...countries].find(m => m.countryCode === formik.values.addressInfo?.countryCode)?.states?.map((m) => (
                  <option value={m.stateCode} >{m.name}</option>
                ))}
              </select>
            </FormControl>
            {formik.errors.addressInfo?.stateCode && (
              <FormError>
                {formik.errors.addressInfo?.stateCode}
              </FormError>
            )}
          </FlexAuto>
        </FormGroup>
        <FormGroup className="mb-4">
          <FormLabel htmlFor="provinceName">City</FormLabel>
          <FlexAuto>
            <FormControl>
              <input id="provinceName"
                name="addressInfo.provinceName"
                type="text"
                placeholder="City"
                onChange={formik.handleChange}
                value={formik.values.addressInfo?.provinceName} />
            </FormControl>
            {formik.errors.addressInfo?.provinceName && (
              <FormError>
                {formik.errors.addressInfo?.provinceName}
              </FormError>
            )}
          </FlexAuto>
        </FormGroup>
        <FormGroup className="mb-4">
          <FormLabel htmlFor="postalCode">Postal Code</FormLabel>
          <FlexAuto>
            <FormControl>
              <input id="postalCode"
                name="addressInfo.postalCode"
                type="text"
                placeholder="Postal Code"
                onChange={formik.handleChange}
                value={formik.values.addressInfo?.postalCode} />
            </FormControl>
            {formik.errors.addressInfo?.postalCode && (
              <FormError>
                {formik.errors.addressInfo?.postalCode}
              </FormError>
            )}
          </FlexAuto>
        </FormGroup>
        <FormGroup className="mb-4">
          <FormLabel htmlFor="address">Address</FormLabel>
          <FlexAuto>
            <FormControl>
              <textarea id="address"
                name="addressInfo.street"
                placeholder="Address"
                onChange={formik.handleChange}
                value={formik.values.addressInfo?.street} />
            </FormControl>
            {formik.errors.addressInfo?.street&& (
              <FormError>
                {formik.errors.addressInfo?.street}
              </FormError>
            )}
          </FlexAuto>
        </FormGroup>
        <FormGroup className="mb-4">
          <FormLabel htmlFor="button" ></FormLabel>
          <FlexAuto>
            <PrimaryButton className="inline-block mt-4" type="submit" >Save</PrimaryButton>
          </FlexAuto>
        </FormGroup>
      </form>
    </MainContainer>
  );
};

export default AddUpdateAddressPage;
